import React, { useMemo } from "react";

import { faCrown } from "@fortawesome/pro-solid-svg-icons/faCrown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnType } from "antd/es/table";
import { IntlShape } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import { AvatarWithName, TeamAvatarWithName } from "@mapmycustomers/ui";

import UserPreviewCard from "@app/component/UserPreviewCard";
import RankDiff from "@app/scene/dashboard/components/Sidebar/components/RankDiff";
import leaderboardFieldModel from "@app/util/fieldModel/LeaderboardFieldModel";
import getLeaderboardFieldValue from "@app/util/leaderboard/getLeaderboardFieldValue";

import formatMetricValue from "./formatMetricValue";
import styles from "./useColumns.module.scss";

const useColumns = (intl: IntlShape, columns: ColumnModel, rankingField: IField) =>
  useMemo<ColumnType<LeaderboardItem>[]>(() => {
    const visibleFieldsNames = new Set(
      columns.filter(({ visible }) => visible).map(({ field }) => field.name)
    );

    return [
      {
        dataIndex: "rank",
        fixed: "left",
        render: (rank: number | undefined) => (
          <div className={styles.rank}>
            {rank === 1 ? <FontAwesomeIcon className={styles.crown} icon={faCrown} /> : rank}
          </div>
        ),
        width: "64px",
      },
      {
        dataIndex: "name",
        fixed: "left",
        render: (_, data) => {
          return data.team ? (
            <div className={styles.rankDiff}>
              <TeamAvatarWithName avatarClassName={styles.avatar} team={data.team} />
              <RankDiff rankDiff={data.rankDiff} />
            </div>
          ) : (
            data.user && (
              <div className={styles.rankDiff}>
                <UserPreviewCard userId={data.user.id}>
                  <div>
                    <AvatarWithName
                      actionOnHover
                      avatarClassName={styles.avatar}
                      user={data.user}
                    />{" "}
                  </div>
                </UserPreviewCard>
                <RankDiff rankDiff={data.rankDiff} />
              </div>
            )
          );
        },
        title: intl.formatMessage({
          id: "leaderboard.listView.table.column.name",
          defaultMessage: "Name",
          description: "Name of a details column in activity table on activity page",
        }),
        width: "300px",
      },
      ...leaderboardFieldModel.fields
        .filter(
          (field) => field.hasFeature(FieldFeature.METRIC) && visibleFieldsNames.has(field.name)
        )
        .map<ColumnType<LeaderboardItem>>((field) => ({
          dataIndex: ["values", field.name],
          defaultSortOrder: rankingField.name === field.name ? "descend" : undefined,
          fixed: rankingField.name === field.name ? "left" : undefined,
          render: (_, data) => {
            const value = getLeaderboardFieldValue(field.name as LeaderboardMetricFieldName, data);
            return formatMetricValue(intl, field, value);
          },
          sorter: (a, b) =>
            getLeaderboardFieldValue(field.name as LeaderboardMetricFieldName, a) -
            getLeaderboardFieldValue(field.name as LeaderboardMetricFieldName, b),
          title: field.displayName,
          width: "200px",
        }))
        // put fixed column(s) to be in the beginning
        .sort((a, b) => (a.fixed ? -1 : b.fixed ? 1 : 0)),
    ];
  }, [intl, columns, rankingField]);

export default useColumns;
